import tagConstants from "constants/Tag";
import { SYSTEM_ROLES } from "constants/Constants";
import useAuthStore from "stores/useAuthStore";

const { metadataTypes } = tagConstants;

export const canReadField = field => {
  const roleId = useAuthStore.getState().userAuth?.role?.id;

  if (roleId === SYSTEM_ROLES.ADMIN) {
    return true;
  }

  if (field.read_roles?.length > 0) {
    return field.read_roles.includes(roleId) || field.read_roles.includes(0);
  }

  // Fields saved before read_roles existed carry only required_roles.
  if (field.required_roles?.length > 0) {
    return (
      field.required_roles.includes(roleId) || field.required_roles.includes(0)
    );
  }

  return true;
};

export const canEditField = field => {
  const roleId = useAuthStore.getState().userAuth?.role?.id;

  if (roleId === SYSTEM_ROLES.ADMIN || !field.edit_roles?.length) {
    return true;
  }

  return field.edit_roles.includes(roleId);
};

export const initFields = tag =>
  tag.tag_metadata_fields
    .filter(field => canReadField(field) || canEditField(field))
    .map(field => {
      const value = tag.tag_metadata_values?.find(
        metadataValue => metadataValue.field_id === field.id
      )?.value;
      return {
        ...field,
        value: value || field.default_value,
        canEdit: canEditField(field),
      };
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

// A field the user cannot change is shown, but its value is sent back as it was.
export const isFieldLocked = field => !field.allow_override || !field.canEdit;

export const isMultiValued = field =>
  field.field_type === metadataTypes.list ||
  (field.field_type === metadataTypes.categorical &&
    field.field_options?.type === "multi");

export const metadataFieldKey = field => `field_${field.id}`;

// Stored list values arrive as a Postgres array literal ("{a,b}"), and the
// edited ones as an array.
const parseListValue = value => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  const inner = (value || "{}").replace(/^{|}$/g, "");
  return inner
    ? inner.split(",").map(e => e.replace(/^[\\"]+|[\\"]+$/g, ""))
    : [];
};

export const toFormValue = field => {
  if (field.field_type === metadataTypes.boolean) {
    return field.value === true || field.value === "true";
  }
  if (isMultiValued(field)) return parseListValue(field.value);
  if (field.field_type === metadataTypes.categorical) {
    return field.value || null;
  }
  return field.value ?? "";
};

// Booleans go back as the strings the stored values use, and multi-valued
// fields as arrays, which is what the tag endpoints accept.
export const toMetadataValue = (field, formValue) => {
  if (field.field_type === metadataTypes.boolean) return String(formValue);
  if (isMultiValued(field)) return formValue;
  return formValue ?? "";
};
